import { datadogRum } from '@datadog/browser-rum';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import CheckTime from '../../../../components/CheckTime/CheckTime';
import Policies from '../../../../components/Policies/Policies';
import useFormattedRoomName from '../../../../hooks/useFormattedRoomName/useFormattedRoomName';
import useRateContent from '../../../../hooks/useRateContent/useRateContent';

const RoomPolicies = ({ booking, showCommission }) => {
  const { rateContent, loading } = useRateContent(
    booking.roomRate.code,
    booking.productCode,
    booking
  );

  const formattedRoomName = useFormattedRoomName(
    booking.roomType.category.name,
    booking.roomType.title,
    booking.productCode
  );

  const hotel = useSelector(
    (state) => state.belmond.hotels[booking.productCode]
  );

  const additionalPolicy = useSelector(
    (state) => state.appSettings.featureFlags.additionalPolicy
  );

  // Check for missing policy list when not loading
  // this is temporary and will help us find sessions where this may fail

  useEffect(() => {
    const policies = rateContent?.policyList?.[booking.roomType.code];
    const policiesExist = Object.keys(policies || {}).length;

    if (!loading && !policiesExist && rateContent?.policyList) {
      const error = new Error('No policies found for room type');
      datadogRum.addError(error, {
        // Current booking info
        productCode: booking.productCode,
        roomTypeCode: booking.roomType.code,
        roomRateCode: booking.roomRate.code,

        // Rate content existence checks
        rateContentExists: !!rateContent,
        policyListExists: !!rateContent?.policyList,

        // Available room types and policies
        availableRoomTypes: Object.keys(rateContent?.policyList || {}),
        policyKeysForRoom: Object.keys(policies || {}),

        // Room type details
        roomTypeTitle: booking.roomType.title,
        roomTypeCategory: booking.roomType.category?.name,

        // Policy list structure
        policyListStructure: JSON.stringify(rateContent?.policyList),

        // Additional context
        loadingState: loading,
        hasRoomTypeCode:
          booking.roomType.code in (rateContent?.policyList || {}),
        policiesLength: policiesExist,

        // Timestamps to help debug race conditions
        timestamp: new Date().toISOString(),

        // Full paths check
        fullRoomTypePath: `${booking.roomType.code} -> ${JSON.stringify(
          rateContent?.policyList?.[booking.roomType.code]
        )}`,
      });
    }
  }, [loading, booking, rateContent]);

  if (loading)
    return <Skeleton height={100} containerTestId="loading-skeleton" />;

  return (
    <>
      <hr className="bbe-hr my-4" />
      <h3>{formattedRoomName}</h3>
      <CheckTime
        checkInTime={hotel.checkInTime}
        checkOutTime={hotel.checkOutTime}
      />
      <div className="mt-3">
        <Policies
          policies={rateContent.policyList?.[booking.roomType.code]}
          loading={loading}
          showCommission={showCommission}
          additionalPolicyText={
            additionalPolicy?.enabled
              ? additionalPolicy?.metadata?.policyText
              : ''
          }
        />
      </div>
    </>
  );
};

RoomPolicies.propTypes = {
  booking: PropTypes.object.isRequired,
  showCommission: PropTypes.bool,
};

export default RoomPolicies;
